import moment from 'moment'
import { toast } from 'sonner'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MediaItem } from '@/types/feed.type'
import { firebaseRtdb } from '@/lib/firebase'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { PaginateResponse } from '@/utils/paginate'
import { Skeleton } from '@/components/ui/skeleton'
import { ref, onValue, off } from 'firebase/database'
import { Separator } from '@/components/ui/separator'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import InfiniteScroll from 'react-infinite-scroll-component'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LightboxDialog from '@/components/share-components/lightbox-dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Play,
  Square,
  Copy,
  AlertTriangle,
  Radio,
  MessageSquareCode,
  Plus,
  VideoOff,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

import type { Program, ProgramRecord } from './program.types'

import LogDialog from './log-dialog'
import FeedFormDialog from './feed-form-dialog'
import ProgramDetailCard from './program-detail-card'
import ProgramPreviewCard from './program-preview-card'
import VideoFileCard from './components/video-file-card'
import { ProgramStatus, ProgramType } from './program.types'
// Automation recording disabled.
// Keep this sample for future restore:
// import ProgramRecordStatusCard from './program-record-status-card'

interface ProgramFormProps {
  program?: Program // For edit mode
  onProgramUpdate?: (updatedProgram: Program) => void // Callback for program updates
}

export default function ProgramManage({
  program,
  onProgramUpdate,
}: ProgramFormProps) {
  const openFeedFormDialog = useBoolean(false)
  const isStopping = useBoolean(false)

  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingLoading, setStreamingLoading] = useState(false)
  const [streamKeyLoading, setStreamKeyLoading] = useState(false)
  const [selectedRecordUrl, setSelectedRecordUrl] = useState<string | null>(
    null,
  )
  // const [hasStreamKey, setHasStreamKey] = useState(!!program?.streamKey)
  const [openFilesDialog, setOpenFilesDialog] = useState(false)
  const [updatingFiles, setUpdatingFiles] = useState<Record<string, boolean>>(
    {},
  )
  const [openLogDialog, setOpenLogDialog] = useState(false)

  const [files, setFiles] = useState<ProgramRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<{
    id: string
    url: string
  } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [editName, setEditName] = useState<{
    id: string
    displayName: string
  } | null>(null)
  const [streamKeyDuration, setStreamKeyDuration] = useState(24)
  const [streamKeyDialogOpen, setStreamKeyDialogOpen] = useState(false)

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([])

  // Function to fetch updated program data
  const fetchUpdatedProgram = async () => {
    if (!program?.id) return

    try {
      const response = await axios.get<Program>(
        endpoints.programs.detail(program.id),
      )
      if (response.data && onProgramUpdate) {
        onProgramUpdate(response.data)
      }
    } catch (error) {
      console.error('Error fetching updated program:', error)
      toast.error('ไม่สามารถอัปเดตข้อมูลโปรแกรมได้')
    }
  }

  const fetchRecords = async (pageNumber = 1, append = false) => {
    setLoading(pageNumber === 1)
    try {
      const { data } = await axios.get<PaginateResponse<ProgramRecord>>(
        // tab === 'timeline' ? endpoints.feeds.timeline : endpoints.feeds.list,
        endpoints.programs.programRecord,
        {
          params: {
            page: pageNumber,
            limit: 10,
            programId: program?.id,
            isDeleted: false,
          },
        },
      )
      setFiles((prev) => (append ? [...prev, ...data.items] : data.items))
      setHasMore(pageNumber * data.limit < data.total)
    } catch {
      // TODO: handle error (toast)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!program?.id) return
    // TODO delete it later
    setSelectedRecordUrl(null)

    const programStatusRef = ref(firebaseRtdb, `programs/${program.id}/status`)

    const unsubscribe = onValue(
      programStatusRef,
      (snapshot) => {
        const newStatus = snapshot.val()

        setIsStreaming(newStatus === ProgramStatus.LIVE)
      },
      (error) => {
        console.error('Error monitoring program status:', error)
      },
    )

    return () => {
      off(programStatusRef, 'value', unsubscribe)
    }
  }, [program?.id])

  // Update hasStreamKey when program changes
  // useEffect(() => {
  //   setHasStreamKey(!!program?.streamKey)
  // }, [program?.streamKey])

  // Update isStreaming when program.status changes
  useEffect(() => {
    setIsStreaming(program?.status === ProgramStatus.LIVE)
  }, [program?.status])

  useDeepEffect(() => {
    if (openFilesDialog) {
      fetchRecords(1, false)
    } else if (files.length === 0) {
      fetchRecords(1, false)
    }
  }, [openFilesDialog])

  const fetchMoreData = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchRecords(nextPage, true)
    }
  }

  // ตรวจสอบ streamKey หมดอายุ
  const isStreamKeyExpired = () => {
    if (!program?.streamKeyExpiresAt) return false
    const expiresAt = new Date(program.streamKeyExpiresAt)
    const now = new Date()
    return expiresAt < now
  }

  // ตรวจสอบ streamKey ที่ใช้งานได้
  const hasValidStreamKey = () => {
    return !!program?.streamKey && !isStreamKeyExpired()
  }

  const handleStartStreaming = async () => {
    if (!program?.id) {
      toast.error('ไม่พบโปรแกรม')
      return
    }

    if (!hasValidStreamKey()) {
      toast.error('ต้องขอ Stream Key ก่อนเริ่มถ่ายทอดสด')
      return
    }

    try {
      setStreamingLoading(true)
      // TODO: Call API to start streaming
      await axios.post(endpoints.programs.startStreaming(program.id))
      setIsStreaming(true)
      toast.success('เริ่มการถ่ายทอดสดแล้ว')
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      setStreamingLoading(false)
      await fetchUpdatedProgram()
    }
  }

  const handleStopStreaming = async () => {
    isStopping.onTrue()
    if (!program?.id) {
      toast.error('ไม่พบโปรแกรม')
      return
    }

    try {
      setStreamingLoading(true)
      // TODO: Call API to stop streaming
      await axios.post(endpoints.programs.stopStreaming(program.id))
      setIsStreaming(false)
      toast.success('หยุดการถ่ายทอดสดแล้ว')
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      setStreamingLoading(false)
      isStopping.onFalse()
    }
  }

  const handleRequestStreamKey = async () => {
    if (!program?.id) {
      toast.error('ไม่พบโปรแกรม')
      return
    }

    // Validate duration
    if (streamKeyDuration < 1 || streamKeyDuration > 48) {
      toast.error('อายุ Stream Key ต้องอยู่ระหว่าง 1-48 ชั่วโมง')
      return
    }

    try {
      setStreamKeyLoading(true)
      const response = await axios.get<Program>(
        endpoints.programs.getStreamKey(program.id),
        {
          params: {
            duration: streamKeyDuration,
          },
        },
      )

      console.log('response', response.data)

      // อัปเดต program state ด้วยข้อมูลใหม่
      if (response.data) {
        // อัปเดต program ใน parent component โดยการ fetch ข้อมูลใหม่
        if (onProgramUpdate) {
          onProgramUpdate(response.data)
        } else {
          // Fallback: ใช้ fetchUpdatedProgram แทน window.location.reload()
          await fetchUpdatedProgram()
        }
      }

      toast.success('ขอ Stream Key ใหม่สำเร็จ')
      setStreamKeyDialogOpen(false) // ปิด dialog เมื่อสำเร็จ
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      setStreamKeyLoading(false)
    }
  }

  const handleCopyToClipboard = async (
    text: string,
    label: string,
    e?: React.MouseEvent,
  ) => {
    // ป้องกันการเด้งกลับไปหน้า programs-view.tsx
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    try {
      await navigator.clipboard.writeText(text)
      toast.success(`คัดลอก ${label} เรียบร้อย`)
    } catch (error) {
      console.error('Failed to copy:', error)
      toast.error('ไม่สามารถคัดลอกได้')
    }
  }

  const handleUpdateFile = async (
    id: string,
    fileUrl: string,
    isEnabled?: boolean,
    displayName?: string,
    isDelete?: boolean,
  ) => {
    try {
      if (!id) {
        toast.error('ไม่พบโปรแกรม')
        return
      }

      setUpdatingFiles((prev) => ({ ...prev, [fileUrl]: true }))
      if (isDelete) {
        await axios.delete(endpoints.programs.programRecordById(id))

        setFiles((prev) => prev.filter((file) => file.fileUrl !== fileUrl))
        toast.success('ลบไฟล์สำเร็จ')
      } else {
        console.log({ isEnabled })
        await axios.patch(endpoints.programs.programRecordById(id), {
          url: fileUrl,
          ...(typeof isEnabled === 'boolean' && { isEnabled }),
          ...(displayName && { displayName }),
        })

        setFiles((prev) =>
          prev.map((file) =>
            file.fileUrl === fileUrl
              ? {
                  ...file,
                  ...(typeof isEnabled === 'boolean' && { isEnabled }),
                  ...(displayName && { displayName }),
                }
              : file,
          ),
        )
        toast.success('อัปเดตไฟล์สำเร็จ')
      }
    } catch (error) {
      console.error('Error updating file:', error)
      toast.error(fError(error))
    } finally {
      setUpdatingFiles((prev) => ({ ...prev, [fileUrl]: false }))
    }
  }

  const renderAction = () => {
    if (!program) return null

    return (
      <Card className="border rounded-lg">
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="text-base sm:text-lg font-semibold text-black">
            การตั้งค่าสตรีม
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pt-0 space-y-3 sm:space-y-4">
          {/* Streaming Controls */}

          {program.streamKey && (
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm font-medium text-gray-700">
                การควบคุมการถ่ายทอดสด
              </Label>
              <div className="grid grid-cols-2 gap-2 items-center">
                {hasValidStreamKey() && !isStreaming ? (
                  <Button
                    onClick={handleStartStreaming}
                    disabled={streamingLoading}
                    className="bg-green-600 hover:bg-green-700 text-white w-full h-9 sm:h-10 text-xs sm:text-sm"
                  >
                    {streamingLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    <span className="ml-1">เริ่มถ่ายทอดสด</span>
                  </Button>
                ) : hasValidStreamKey() && isStreaming ? (
                  <Button
                    onClick={handleStopStreaming}
                    disabled={streamingLoading}
                    variant="destructive"
                    className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                  >
                    {streamingLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    <span className="ml-1">หยุดถ่ายทอดสด</span>
                  </Button>
                ) : (
                  <Button
                    disabled
                    className={`${
                      isStreamKeyExpired()
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    } w-full h-9 sm:h-10 text-xs sm:text-sm`}
                  >
                    {streamingLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    <span className="ml-1">เริ่มถ่ายทอดสด</span>
                  </Button>
                )}
                <Button
                  onClick={(e) => {
                    e.preventDefault()
                    setOpenLogDialog(true)
                  }}
                  variant="outline"
                  className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                >
                  {streamKeyLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <MessageSquareCode className="w-4 h-4" />
                  )}
                  <span className="ml-1">ดู Log</span>
                </Button>
              </div>
              <p className="text-xs text-gray-600">
                {!hasValidStreamKey()
                  ? isStreamKeyExpired()
                    ? 'Stream Key หมดอายุแล้ว กรุณาขอใหม่'
                    : 'ต้องขอ Stream Key ก่อนเริ่มถ่ายทอดสด'
                  : isStreaming
                    ? 'กำลังถ่ายทอดสด'
                    : 'เริ่มถ่ายทอดสด'}
              </p>
            </div>
          )}

          {/* ------------------------------ Request Stream Key ------------------------------ */}

          <Dialog
            open={streamKeyDialogOpen}
            onOpenChange={setStreamKeyDialogOpen}
          >
            <DialogTrigger asChild disabled={streamKeyLoading} className="mb-1">
              <Button
                disabled={streamKeyLoading}
                size="sm"
                className={`shrink-0 h-9 sm:h-10 text-xs sm:text-sm w-full ${
                  isStreamKeyExpired()
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                onClick={() => setStreamKeyDialogOpen(true)}
              >
                {streamKeyLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                <span className="ml-1 hidden sm:inline">
                  สร้าง Stream Key {program.streamKey ? 'ใหม่' : ''}
                </span>
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby="" className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  สร้าง Stream Key {program.streamKey ? 'ใหม่' : ''}
                </DialogTitle>
                <DialogDescription>
                  อายุ Stream Key ต้องอยู่ระหว่าง 1-48 ชั่วโมง
                </DialogDescription>
              </DialogHeader>
              <div>
                <Input
                  type="number"
                  id="stream-key-duration"
                  min={1}
                  max={48}
                  step={1}
                  value={streamKeyDuration}
                  onChange={(e) =>
                    setStreamKeyDuration(parseInt(e.target.value))
                  }
                  className="bg-background w-full text-end"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {(streamKeyDuration < 1 || streamKeyDuration > 48) && (
                  <p className="text-xs text-red-500 mt-1">
                    อายุ Stream Key ต้องอยู่ระหว่าง 1-48 ชั่วโมง
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setStreamKeyDialogOpen(false)}
                >
                  ยกเลิก
                </Button>
                <Button
                  type="button"
                  onClick={handleRequestStreamKey}
                  disabled={
                    streamKeyLoading ||
                    !streamKeyDuration ||
                    streamKeyDuration < 1 ||
                    streamKeyDuration > 48
                  }
                >
                  <span>สร้าง{program.streamKey ? 'ใหม่' : ''}</span>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ------------------------------ End Request Stream Key ------------------------------ */}

          {/* Stream Key */}
          {program.streamKey && (
            <>
              <div className="space-y-2">
                <div className="space-y-1 pt-3">
                  <Label className="text-xs sm:text-sm font-medium text-gray-700">
                    Stream Key
                  </Label>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex-1">
                        <Input
                          value={program.streamKey || 'ยังไม่มี Stream Key'}
                          readOnly
                          className={`bg-gray-50 text-gray-600 flex-1 text-xs sm:text-sm h-9 sm:h-10 ${
                            program.streamKey && isStreamKeyExpired()
                              ? 'border-red-300 bg-red-50'
                              : ''
                          }`}
                        />
                      </div>
                      <div>
                        {program.streamKey && (
                          <Button
                            type="button"
                            onClick={(e) =>
                              handleCopyToClipboard(
                                program.streamKey!,
                                'Stream Key',
                                e,
                              )
                            }
                            size="sm"
                            variant="outline"
                            className="shrink-0 h-9 sm:h-10 text-xs sm:text-sm"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {program.streamKey && !isStreamKeyExpired() && (
                  <p className="text-xs flex items-center gap-1 text-gray-500">
                    Stream Key จะหมดอายุเมื่อ{' '}
                    {moment(program.streamKeyExpiresAt).format(
                      'DD/MM/yyyy, hh:mm',
                    )}
                  </p>
                )}

                {program.streamKey && isStreamKeyExpired() && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Stream Key หมดอายุแล้ว กรุณาขอใหม่
                  </p>
                )}
              </div>

              <Separator />

              {/* Stream Server URL */}
              <div className="space-y-1">
                <Label className="text-xs sm:text-sm font-medium text-gray-700">
                  Stream Server URL
                </Label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <Input
                    value={
                      program.streamServerUrl ||
                      'rtmp://your-stream-server.com/live'
                    }
                    readOnly
                    className="bg-gray-50 text-gray-600 flex-1 text-xs sm:text-sm h-9 sm:h-10"
                  />
                  <Button
                    type="button"
                    onClick={(e) =>
                      handleCopyToClipboard(
                        program.streamServerUrl ||
                          'rtmp://your-stream-server.com/live',
                        'Stream Server URL',
                        e,
                      )
                    }
                    size="sm"
                    variant="outline"
                    className="shrink-0 h-9 sm:h-10 text-xs sm:text-sm"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  ใช้สำหรับตั้งค่าในโปรแกรมถ่ายทอดสด (OBS, Streamlabs, etc.)
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    )
  }

  const renderRecord = () => {
    if (!program) return null

    return (
      <Card className="border rounded-lg">
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="text-base sm:text-lg font-semibold text-black flex items-center gap-2">
            <Radio className="h-4 w-4 sm:h-5 sm:w-5" />
            ไฟล์วิดีโอย้อนหลัง
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pt-0 space-y-3">
          <Dialog
            open={openFilesDialog}
            onOpenChange={(e) => {
              if (mediaItems.length <= 0) {
                setOpenFilesDialog(e)
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="w-full h-9 sm:h-10 text-sm">
                แสดงทั้งหมด
              </Button>
            </DialogTrigger>

            <DialogContent
              aria-describedby=""
              className="p-0 max-w-[95vw] sm:max-w-[800px] w-[95dvw] sm:w-[95dvw] h-[90vh] sm:h-[95vh] gap-0"
            >
              <DialogHeader className="p-4 sm:p-6 pb-0">
                <DialogTitle className="truncate text-base sm:text-lg">
                  <div className="flex items-center gap-2">
                    <Radio className="h-4 w-4 sm:h-5 sm:w-5" />
                    ไฟล์วิดีโอย้อนหลัง
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div
                id="scrollableRecordDiv"
                className="w-full h-full overflow-auto flex flex-col gap-3 sm:gap-4 p-3 sm:p-4"
              >
                <InfiniteScroll
                  scrollableTarget="scrollableRecordDiv"
                  dataLength={files.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={
                    <div className="text-center">
                      <div className="space-y-3 sm:space-y-4">
                        {[...Array(3)].map((_, i) => (
                          <Skeleton
                            key={i}
                            className="h-40 sm:h-50 w-full rounded-xl"
                          />
                        ))}
                      </div>
                    </div>
                  }
                  endMessage={
                    <>
                      {!!files.length && (
                        <p className="text-center py-6 text-gray-400 text-sm">
                          ไม่มีไฟล์เพิ่มเติม
                        </p>
                      )}
                    </>
                  }
                >
                  {files.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
                      <VideoOff className="h-12 w-12" strokeWidth={1.5} />
                      <p className="text-base font-medium text-gray-600">
                        ยังไม่มีไฟล์วิดีโอย้อนหลัง
                      </p>
                      <p className="text-sm text-gray-500">
                        ไฟล์ที่ตัดจากการบันทึกสดจะแสดงที่นี่
                      </p>
                    </div>
                  ) : (
                    files.map((file) => (
                      <VideoFileCard
                        key={file.id}
                        file={file}
                        isInDialog
                        isUpdating={!!updatingFiles[file.fileUrl]}
                        editName={editName}
                        onEditNameChange={setEditName}
                        onUpdateFile={handleUpdateFile}
                        onPreview={(f) => {
                          setMediaItems([{ type: 'video', url: f.fileUrl }])
                          setLightboxIndex(0)
                          setLightboxOpen(true)
                        }}
                        onRequestDelete={(f) =>
                          setConfirmDeleteOpen({
                            id: f.id,
                            url: f.fileUrl,
                          })
                        }
                      />
                    ))
                  )}
                </InfiniteScroll>
              </div>
            </DialogContent>
          </Dialog>

          {mediaItems.length > 0 && (
            <LightboxDialog
              fullScreen
              isOpen={lightboxOpen}
              onOpenChange={(e) => {
                if (!e) {
                  setMediaItems([])
                }
                setLightboxOpen(e)
              }}
              mediaItems={mediaItems}
              initialIndex={lightboxIndex}
            />
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* TODO: need fix ui */}
      {program && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 w-full">
            <div className="space-y-4 sm:space-y-6">
              {program.type === ProgramType.ADMIN && renderAction()}

              {/* Automation recording disabled.
                  Keep this sample for future restore:
                  {program.type === ProgramType.ADMIN && (
                    <ProgramRecordStatusCard
                      program={program}
                      isStopping={isStopping.value}
                    />
                  )}
              */}

              {renderRecord()}
              <ProgramDetailCard program={program} />
            </div>

            <ProgramPreviewCard program={program} />
          </div>
        </>
      )}

      <FeedFormDialog
        open={openFeedFormDialog.value}
        onClose={openFeedFormDialog.onFalse}
        videoUrl={selectedRecordUrl || undefined}
      />

      {program?.channelName && (
        <LogDialog
          cname={program.channelName}
          open={openLogDialog}
          onClose={() => setOpenLogDialog(false)}
        />
      )}

      <ConfirmDialog
        open={!!confirmDeleteOpen}
        onOpenChange={(e) => {
          if (!e) {
            setConfirmDeleteOpen(null)
          }
        }}
        title="คุณต้องการลบไฟล์นี้หรือไม่?"
        description="การลบไม่สามารถย้อนกลับได้"
        confirmText="ลบไฟล์"
        cancelText="ยกเลิก"
        onConfirm={async () => {
          try {
            if (confirmDeleteOpen) {
              setIsDeleting(true)
              await handleUpdateFile(
                confirmDeleteOpen.id,
                confirmDeleteOpen.url,
                undefined,
                undefined,
                true,
              )
            }
          } catch {
            toast.error('เกิดข้อผิดพลาดในการลบไฟล์')
          } finally {
            setIsDeleting(false)
          }
        }}
        loading={isDeleting}
      />
    </div>
  )
}
